import React, { useEffect, useState } from "react";
import { Button, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography, Alert, Box, TablePagination, IconButton, Collapse, Chip } from "@mui/material";
import { useStore } from "../../app/stores/store";
import { useTranslation } from "react-i18next";
import ConfirmComponent from "../../app/common/form/ConfirmComponent";
import agent from "../../app/api/agent";
import { ActivityLog, ActivityChange } from "../../app/models/activityLog";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

export default function EmailTemplatesPage() {
  const emailTemplateStore = useStore((s) => s.emailTemplateStore);
  const modalStore = useStore((s) => s.modalStore);
  const [open, setOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<any>(null);
  const [templateLogs, setTemplateLogs] = useState<ActivityLog[]>([]);
  const [logsPage, setLogsPage] = useState(0);
  const [logsPageSize, setLogsPageSize] = useState(5);
  const [logsTotalRecords, setLogsTotalRecords] = useState(0);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const { t } = useTranslation();

  useEffect(() => {
    emailTemplateStore.fetchTemplates();
  }, [emailTemplateStore.fetchTemplates]);

  useEffect(() => {
    const loadTemplateLogs = async () => {
      const response = await agent.ActivityLogs.getEmailTemplateActivityLogs({
        page: logsPage + 1,
        pageSize: logsPageSize,
        orderBy: "CreatedAt",
        sortDirection: "desc",
      });

      setTemplateLogs(response.data || []);
      setLogsTotalRecords(response.totalRecords || 0);
    };

    loadTemplateLogs();
  }, [logsPage, logsPageSize]);

  const handleEdit = (template: any) => {
    setEditingTemplate(template);
    setOpen(true);
  };

  const handleSave = async () => {
    if (editingTemplate.id) {
      await emailTemplateStore.updateTemplate(editingTemplate);
    } else {
      await emailTemplateStore.createTemplate(editingTemplate);
    }
    setLogsPage(0);
    setOpen(false);
    setEditingTemplate(null);
  };

  const handleDelete = (id: string) => {
    modalStore.openModal(
      <ConfirmComponent
        text={t("general.areYouSure")}
        onYes={async () => {
          await emailTemplateStore.deleteTemplate(id);
          setLogsPage(0);
          modalStore.closeModal();
        }}
        onCancel={modalStore.closeModal}
      />
    );
  };

  const handleAdd = () => {
    setEditingTemplate({ subject: "", body: "" });
    setOpen(true);
  };

  const toggleExpandRow = (activityId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(activityId)) {
      newExpanded.delete(activityId);
    } else {
      newExpanded.add(activityId);
    }
    setExpandedRows(newExpanded);
  };

  const parseChanges = (changesJson: string): ActivityChange[] => {
    try {
      return changesJson ? JSON.parse(changesJson) : [];
    } catch {
      return [];
    }
  };

  const formatDateTime = (value?: string) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return new Intl.DateTimeFormat("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleAdd} sx={{ mb: 2 }}>
        {t("emails.templates.add")}
      </Button>
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("emails.templates.subject")}</TableCell>
              <TableCell>Kreirao</TableCell>
              <TableCell>Zadnje ažuriranje</TableCell>
              <TableCell>{t("emails.templates.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {emailTemplateStore.templates.map((tpl) => (
              <TableRow key={tpl.id}>
                <TableCell>{tpl.subject}</TableCell>
                <TableCell>{tpl.createdByUserName || "-"}</TableCell>
                <TableCell>{formatDateTime(tpl.lastUpdatedAt)}</TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => handleEdit(tpl)}
                    sx={{ mr: 1 }}
                  >
                    {t("emails.templates.edit")}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => handleDelete(tpl.id)}
                  >
                    {t("emails.templates.delete")}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("emails.templates.edit")}</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <TextField
              label={t("emails.templates.subject")}
              value={editingTemplate?.subject || ""}
              onChange={e => setEditingTemplate({ ...editingTemplate, subject: e.target.value })}
              fullWidth
              variant="standard"
              sx={{ mb: 2 }}
            />
            <TextField
              label={t("emails.templates.body")}
              value={editingTemplate?.body || ""}
              onChange={e => setEditingTemplate({ ...editingTemplate, body: e.target.value })}
              fullWidth
              multiline
              rows={6}
              variant="standard"
            />
          </Box>
          <Alert severity="info" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {t("emails.templates.variablesTitle")}
            </Typography>
            <Typography variant="body2">
              {t("emails.templates.variablesDescription")}
            </Typography>
            <ul style={{ marginLeft: 16 }}>
              <li>{t("emails.templates.variableMemberNumber")}</li>
              <li>{t("emails.templates.variableFirstName")}</li>
              <li>{t("emails.templates.variableLastName")}</li>
            </ul>
          </Alert>
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {t("emails.templates.variableCustomTitle")}
            </Typography>
            <Typography variant="body2">
              {t("emails.templates.variableCustomDescription")}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSave} variant="contained" color="success">
            {t("emails.templates.save")}
          </Button>
          <Button onClick={() => setOpen(false)} variant="outlined">
            {t("emails.templates.cancel")}
          </Button>
        </DialogActions>
      </Dialog>

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        Aktivnosti predložaka e-mailova
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 50 }} />
              <TableCell>Akcija</TableCell>
              <TableCell>Korisnik</TableCell>
              <TableCell>Detalji</TableCell>
              <TableCell align="right">Vrijeme</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templateLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {t("dataTable.noData")}
                </TableCell>
              </TableRow>
            ) : (
              templateLogs.map((log) => [
                <TableRow key={log.id} hover>
                  <TableCell style={{ width: 50 }}>
                    {log.changes && (
                      <IconButton
                        size="small"
                        onClick={() => toggleExpandRow(log.id!)}
                      >
                        {expandedRows.has(log.id!) ? (
                          <KeyboardArrowUpIcon />
                        ) : (
                          <KeyboardArrowDownIcon />
                        )}
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell>{log.name}</TableCell>
                  <TableCell>{log.createdByUserName || "System"}</TableCell>
                  <TableCell>{log.note}</TableCell>
                  <TableCell align="right">
                    {log.createdAt ? new Date(log.createdAt).toLocaleString("de-DE") : ""}
                  </TableCell>
                </TableRow>,
                log.changes && expandedRows.has(log.id!) && (
                  <TableRow key={`${log.id}-details`}>
                    <TableCell colSpan={5} sx={{ py: 0, backgroundColor: "#fafafa" }}>
                      <Collapse in={true} timeout="auto" unmountOnExit>
                        <Box sx={{ m: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                          {parseChanges(log.changes).map((change, idx) => (
                            <Box key={idx} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                              <Chip label={change.fieldLabel || change.field} size="small" variant="outlined" sx={{ minWidth: 120 }} />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2"><strong>Staro:</strong> {change.oldValue}</Typography>
                                <Typography variant="body2" color="primary"><strong>Novo:</strong> {change.newValue}</Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                ),
              ]).filter(Boolean)
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={logsTotalRecords}
        rowsPerPage={logsPageSize}
        page={logsPage}
        onPageChange={(_, page) => setLogsPage(page)}
        onRowsPerPageChange={(e) => {
          setLogsPageSize(parseInt(e.target.value, 10));
          setLogsPage(0);
        }}
      />
    </>
  );
}
